const AUTH_TOKEN = "authToken";
const REFRESH_TOKEN = "refreshToken";
import { TOKEN_TIME_BEFORE_EXPIRATION } from "@/constants/util.constants";
import { clearAllLocFor } from "@/models/localForage/localForage.model";
import {
  AuthTokenDetails,
  authTokenDetailsSchema,
  RoleName,
  roleNameSchema,
} from "@/types";
import { jwtDecode, JwtPayload } from "jwt-decode";

export function getLSRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN);
}

export function setLSRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN, token);
}

export function getLSAuthToken(): string | null {
  return localStorage.getItem(AUTH_TOKEN);
}

export function setLSAuthToken(token: string): void {
  localStorage.setItem(AUTH_TOKEN, token);
}

export function utilLogOut() {
  utilClearAllLocalStorage();
  window.location.href = "/login";
}

export function utilClearAllLocalStorage() {
  localStorage.clear();
  sessionStorage.clear();
  clearAllLocFor();
}

export function utilClearLoginLocalStorage() {
  localStorage.removeItem(AUTH_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  sessionStorage.clear();
  clearAllLocFor();
}

// #region isTokenAboutToExpire
// Check if the jwt is about to expire (Tentative Timing: 5 minutes)
export function isTokenAboutToExpire(token: string): boolean {
  try {
    const decoded: JwtPayload = jwtDecode(token);

    // log formatted date
    // console.log(
    //   new Date(decoded.exp * 1000).toLocaleString("UTC", {
    //     timeZone: "UTC",
    //   })
    // );

    // convert remaining time to milliseconds
    const remainingTimeMilliseconds = decoded.exp
      ? // ? Math.floor((decoded.exp * 1000 - Date.now()) / 1000)
        decoded.exp * 1000 - Date.now()
      : 0;

    // console.log("remainingTimeSeconds: " + remainingTimeMilliseconds / 1000);

    // if remaining remainingTimeMilliseconds is less than or equal to TOKEN_TIME_BEFORE_EXPIRATION, then return true
    if (remainingTimeMilliseconds <= TOKEN_TIME_BEFORE_EXPIRATION) {
      // console.log(
      //   "Token is about to expire in " +
      //     remainingTimeMilliseconds / 1000 +
      //     " seconds",
      // );
      return true;
    }

    // totalTimeRemaining : 00:00:00
    const totalTimeRemaining = `${remainingTimeMilliseconds / 1000} seconds`;
    if (!decoded.exp || decoded.exp < Date.now() / 1000) {
      console.log("Token expired " + totalTimeRemaining);
      return true;
    } else {
      // console.log("Token valid " + totalTimeRemaining);
      return false;
    }
  } catch (err) {
    console.error(err);
    return true;
  }
}
// #endregion isTokenAboutToExpire

// #region isTokenExpired
// Check if the jwt is not expired
export function isTokenExpired(token: string): boolean {
  try {
    // To decode and get the payload without specifying options
    const decoded: JwtPayload = jwtDecode(token);

    if (
      !decoded ||
      typeof decoded !== "object" ||
      decoded.exp === undefined ||
      decoded.exp === null
    ) {
      console.error("Token is not valid");
      return true;
    }

    // make sure the expiry is a number before doing calculations
    if (!decoded.exp || typeof decoded.exp !== "number" || isNaN(decoded.exp)) {
      console.error("Token Expiry is not a valid number", decoded.exp);
      return true;
    }

    if (!decoded.exp) {
      return true;
    }

    // log formatted date
    // console.log(
    //   new Date(decoded.exp * 1000).toLocaleString("UTC", {
    //     timeZone: "UTC",
    //   })
    // );
    // remainingTimeHours, remainingTimeMinutes, remainingTimeSeconds

    const remainingTimeHours = Math.floor(
      (decoded.exp * 1000 - Date.now()) / (1000 * 60 * 60)
    );
    const remainingTimeMinutes = Math.floor(
      ((decoded.exp * 1000 - Date.now()) / (1000 * 60)) % 60
    );
    const remainingTimeSeconds = Math.floor(
      ((decoded.exp * 1000 - Date.now()) / 1000) % 60
    );
    // totalTimeRemaining : 00:00:00
    const totalTimeRemaining = `${remainingTimeHours}:${remainingTimeMinutes}:${remainingTimeSeconds}`;
    if (decoded.exp < Date.now() / 1000) {
      console.log("Token expired " + totalTimeRemaining);

      return true;
    } else {
      // console.log("Token valid " + totalTimeRemaining);
      return false;
    }
  } catch (err) {
    console.error(err);
    return true;
  }
}
// #endregion isTokenExpired

export function getRoleFromToken(): RoleName | undefined {
  const token = getLSAuthToken();
  if (!token) {
    return undefined;
  }

  try {
    const decoded = jwtDecode(token);

    if (!decoded || typeof decoded !== "object") {
      console.error("Decoded token is not a valid object");
      return undefined;
    }

    if (!("role" in decoded) || !decoded.role) {
      console.error("Role not found in decoded token");
      return undefined;
    }

    const validRole = roleNameSchema.safeParse(decoded.role);

    if (validRole.success) {
      return validRole.data;
    } else {
      console.error("Invalid role in token:", validRole.error);
      return undefined;
    }
  } catch (error) {
    console.error("Error decoding token:", error);
  }

  return undefined;
}

export function setSessionShouldUpdateAccount(
  sessionUpdateAccount: boolean
): void {
  sessionStorage.setItem(
    "sessionShouldUpdateAccount",
    String(sessionUpdateAccount)
  );
}

export function getSessionShouldUpdateAccount(): boolean {
  const sessionUpdateAccount = sessionStorage.getItem(
    "sessionShouldUpdateAccount"
  );
  if (!sessionUpdateAccount) {
    return true; // Default to true if not set
  }
  return sessionUpdateAccount === "true";
}

export function getSessionDismissedUpdateAccount(): boolean {
  const userDismissedUpdateAccount = sessionStorage.getItem(
    "userDismissedUpdateAccount"
  );
  return userDismissedUpdateAccount === "true";
}

export function setSessionDismissedUpdateAccount(
  userDismissedUpdateAccount: boolean
): void {
  sessionStorage.setItem(
    "userDismissedUpdateAccount",
    String(userDismissedUpdateAccount)
  );
}

export function setSessionShouldRegisterAccount(
  sessionShouldRegisterAccount: boolean
): void {
  sessionStorage.setItem(
    "sessionShouldRegisterAccount",
    String(sessionShouldRegisterAccount)
  );
}

export function getSessionShouldRegisterAccount(): boolean {
  const sessionShouldRegisterAccount = sessionStorage.getItem(
    "sessionShouldRegisterAccount"
  );
  if (!sessionShouldRegisterAccount) {
    return true; // Default to true if not set
  }
  return sessionShouldRegisterAccount === "true";
}

export function getUserDetailsFromToken(): AuthTokenDetails | undefined {
  const token = getLSAuthToken();
  if (!token) {
    return undefined;
  }

  try {
    const decoded = jwtDecode(token);

    const parsedDetails = authTokenDetailsSchema.safeParse(decoded);
    if (parsedDetails.success) {
      return parsedDetails.data;
    } else {
      console.error("Invalid token details:", parsedDetails.error);
      return undefined;
    }
  } catch (error) {
    console.error("Error decoding token:", error);
    return undefined;
  }
}
